/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
	Alert,
	Avatar,
	Box,
	Divider,
	Flex,
	Group,
	Image,
	NumberFormatter,
	Paper,
	Skeleton,
	Spoiler,
	Tabs,
	Text,
} from '@mantine/core';
import {
	useElementSize,
	useMediaQuery,
} from '@mantine/hooks';
import { useEffect, useState } from 'react';
import './tabs.css';
// UI local components
import classesTabs from './tabs.module.css';

// Styles
import classes from './measurability-projects.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Router
import { useParams } from 'react-router';

// Api
import { useMeasurabilityProject } from '@features/catalogs/meausrability-projects/details/api/get-public-measurability-project-by-id';

// Icons
import {
	IconInfoCircle,
	IconQuestionMark,
} from '@tabler/icons-react';

// constants
import { NOT_APPLICABLE } from '@config/constants/common';

import { getCountryName } from '@utils/countries';

// Types
import { TFileMetadata } from '@global-types/index';

import { DescriptionRenderer } from '@components/atoms/DescriptionRenderer';
import { Loading } from '@components/atoms/Loading/Loading';
import { DownloadTag } from '@components/v2/ui/DownloadTag/DownloadTag';
import { IconProjectLocation } from '@components/v2/ui/IconProjectLocation/IconProjectLocation';
import { Title } from '@components/v2/ui/Title';
import { getUnitLabel } from '@utils/units';

const DEFAULT_COVER =
	'https://static.vecteezy.com/system/resources/thumbnails/028/142/383/small_2x/a-stadium-filled-with-excited-fans-a-football-field-in-the-foreground-background-with-empty-space-for-text-photo.jpg';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type SidebarLink = {
	id: string;
	label: string;
	path: string;
	icon1: string;
	iconActive1: string;
};

export type SidebarItem = {
	id: string;
	path: string;
	label: string;
	icon: string;
	iconActive: string;
	group: string;
	disabled?: boolean;
	links?: SidebarLink[];
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MeasurabilityProjectsPublic = () => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	const { ref } = useElementSize();

	const { projectId } = useParams();
	const isSmall = useMediaQuery('(max-width: 768px)');

	const icon = <IconInfoCircle />;

	const { data, isFetching, isError, contactProducer } =
		useMeasurabilityProject({
			projectId: projectId ? projectId : '',
		});

	const [isExpanded, setIsExpanded] = useState(false);

	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		setTimeout(() => {
			setIsLoading(false);
		}, 1000);
	}, []);

	if (isLoading) return <Loading />;

	if (isError) {
		return (
			<Box className={classes.container} ref={ref}>
				<Paper className={classes.paperError}>
					<Alert
						variant="light"
						color="error"
						title={t('errors.invalidProjectIdTitle')}
						icon={icon}
					>
						{t('errors.invalidProjectId')}
					</Alert>
				</Paper>
			</Box>
		);
	}

	if (isFetching) {
		return (
			<Box className={classes.containerSkeletons}>
				<Skeleton
					height={'30%'}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'200px'}
					radius="md"
				/>
				<Skeleton
					height={'5%'}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'8%'}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={'5%'}
					width={'100%'}
					radius="md"
				/>
			</Box>
		);
	}
	const measurabilityProject = data;

	if (!measurabilityProject) return null;

	const {
		cover,
		name,
		technology,
		description,
		entity,
		location,
		publicSpecs,
		supportedBy,
		sellPrice_europertco2eq,
		remaining_aco2,
	} = measurabilityProject;
	/* ******************************** RENDERING ******************************* */
	return (
		<Box>
			<Box className={classes.header}>
				<Title
					c="var(--mantine-color-igluPrimary-9)"
					order={4}
					textWrap="wrap"
					data-testid="topbar-title"
				>
					{t('global.measurabilities')}
				</Title>
			</Box>
			<Box
				className={classes.container}
				ref={ref}
				pb={'xl'}
				pl={'xl'}
				pr={'xl'}
				pt={'md'}
			>
				<Box
					style={{
						padding: '24px',
						display: 'flex',
						flex: '1',
						flexDirection: 'column',
						minHeight: '304px',
						maxHeight: 'fit-content',
						borderRadius: '32px',
						position: 'relative',
						backgroundSize: 'cover',
						backgroundPosition: 'center',
						backgroundRepeat: 'no-repeat',
						backgroundImage: `
				  linear-gradient(0deg, rgba(15, 23, 42, 0.00) 0%, #0F172A 104.61%),
				  url(${cover ?? DEFAULT_COVER})
				`,
					}}
				>
					<Flex gap={'20px'}>
						{supportedBy && (
							<Flex>
								<Image
									src={supportedBy}
									w={'89px'}
									h={'89px'}
									radius={'xl'}
									style={{
										border: '4px solid #F0F3F8',
									}}
								/>
							</Flex>
						)}
						<Flex
							direction={'column'}
							flex={1}
							gap={'2px'}
						>
							<Flex wrap={'wrap'}>
								<Flex flex={1} wrap={'wrap'} gap={'20px'}>
									<Flex flex={1} direction={'column'}>
										{supportedBy?.name && (
											<Flex>
												<Text
													variant="body2"
													c="var(--mantine-color-igluTextButton-9)"
												>
													{t('global.projectSupportedBy')}{' '}
													<Text
														span
														variant="body2"
														c="var(--mantine-color-igluSurface-9)"
													>
														{supportedBy?.name ||
															NOT_APPLICABLE}
													</Text>
												</Text>
											</Flex>
										)}
										<Flex>
											<Title
												order={4}
												c="var(--mantine-color-igluSurface-9)"
											>
												{name}
											</Title>
										</Flex>
										<Flex gap={'16px'}>
											<Group justify="center" gap={'5px'}>
												<Avatar
													radius={'xl'}
													src={`https://flagcdn.com/w40/${location?.countryCode?.toLowerCase()}.png`}
													size={18}
													style={{
														border: '1px solid #D7D6D6',
													}}
												>
													<IconQuestionMark size={18} />
												</Avatar>

												<Box>
													<Text
														variant={'caption'}
														c={
															'var(--mantine-color-igluSurface-9)'
														}
													>
														{getCountryName(
															location.countryCode
														)}
													</Text>
												</Box>
											</Group>
											<Group gap={'5px'}>
												<IconProjectLocation
													color={'#FDFDFD'}
												/>

												<Box>
													<Text
														variant={'caption'}
														c={
															'var(--mantine-color-igluSurface-9)'
														}
													>
														{location?.place ||
															NOT_APPLICABLE}
													</Text>
												</Box>
											</Group>
										</Flex>
									</Flex>
								</Flex>
								<Flex
									gap={'16px'}
									align={'center'}
									wrap={'wrap'}
								>
									<Flex direction={'column'} flex={1}>
										<Flex justify={'end'} flex={1}>
											<Title
												order={4}
												c="var(--mantine-color-igluSurface-9)"
												style={{
													whiteSpace: 'nowrap',
												}}
											>
												<NumberFormatter
													thousandSeparator=" "
													decimalSeparator=","
													value={sellPrice_europertco2eq}
													decimalScale={2}
												/>
												<Text
													size="18px"
													span
													c={'#FDFDFD'}
												>
													{` ${t('global.sellPriceUnit')}`}
												</Text>
											</Title>
										</Flex>
										<Flex justify={'end'} flex={1}>
											<Text
												variant="body1"
												c="var(--mantine-color-igluTextButton-9)"
												style={{
													whiteSpace: 'nowrap',
												}}
											>
												{t('global.sellPrice')}{' '}
											</Text>
										</Flex>
									</Flex>
									<Divider
										color="white"
										size="1px"
										orientation="vertical"
									/>
									<Flex direction={'column'} flex={1}>
										<Flex flex={1}>
											<Title
												order={4}
												c="var(--mantine-color-igluSurface-9)"
												style={{
													whiteSpace: 'nowrap',
												}}
											>
												<NumberFormatter
													thousandSeparator=" "
													decimalSeparator=","
													value={remaining_aco2}
													decimalScale={2}
												/>
												<Text
													size="18px"
													span
													c={'#FDFDFD'}
												>
													{` ${getUnitLabel('tco2e')}`}
												</Text>
											</Title>
										</Flex>
										<Flex flex={1}>
											<Text
												style={{
													whiteSpace: 'nowrap',
												}}
												variant="body1"
												c="var(--mantine-color-igluTextButton-9)"
											>
												{t('global.availableStock')}
											</Text>
										</Flex>
									</Flex>
								</Flex>
							</Flex>
							<Flex>
								<Flex flex={1} gap={'16px'}>
									<Text
										variant="body1"
										c="var(--mantine-color-igluTextButton-9)"
									>
										{t('global.type')}
										{' : '}
										<Text
											span
											variant="body2"
											c="var(--mantine-color-igluSurface-9)"
										>
											{t(technology)}
										</Text>
									</Text>
								</Flex>
							</Flex>
						</Flex>
					</Flex>
				</Box>

				<Flex
					style={{
						zIndex: '10',
						borderRadius: '32px',
						padding: '0px 30px',
					}}
					mt={'-60px'}
					direction={'column'}
					flex={1}
				>
					<Tabs
						defaultValue="generalInformations"
						classNames={classesTabs}
					>
						<Tabs.List>
							<Tabs.Tab value="generalInformations">
								{t('global.generalInformations')}
							</Tabs.Tab>
						</Tabs.List>
						{/* Infos générales */}
						<Tabs.Panel
							value="generalInformations"
							p={'20px'}
						>
							<Flex flex={1} gap={'24px'} wrap={'wrap'}>
								<Flex
									style={{
										flex: isSmall ? '1 1 100%' : 'none',
									}}
									bg={'#FDFDFD'}
								>
									<Flex
										className={classes.containerInfoBloc}
										style={{
											maxHeight: 'fit-content',
										}}
									>
										<Title
											order={5}
											c="var(--mantine-color-igluPrimary-9)"
											mb={'24px'}
										>
											{t('global.producerInfo')}
										</Title>
										<Flex
											style={{
												width: '100%',
											}}
											direction={'column'}
											gap={'10px'}
										>
											{entity?.name && (
												<Flex
													style={{
														width: '100%',
													}}
													flex={1}
													gap={'10px'}
												>
													<Flex flex={3}>
														<Text
															span
															variant="body2"
															c="var(--mantine-color-igluSecondary-9)"
														>
															{t('global.name')}
														</Text>
													</Flex>
													<Flex flex={5}>
														<Text
															span
															variant="body2"
															c="var(--mantine-color-igluTextPrimary-9)"
														>
															{entity.name
																? entity.name
																: NOT_APPLICABLE}
														</Text>
													</Flex>
												</Flex>
											)}
											<Flex
												style={{
													width: '100%',
												}}
												flex={1}
												gap={'10px'}
											>
												<Flex flex={3}>
													<Text
														span
														variant="body2"
														c="var(--mantine-color-igluSecondary-9)"
													>
														{t('global.location')}
													</Text>
												</Flex>
												<Flex
													align={'center'}
													gap={'5px'}
													flex={5}
												>
													<Avatar
														className={
															classes.logoCountry
														}
														radius={'xl'}
														src={`https://flagcdn.com/w40/${entity.location && entity.location.countryCode ? entity.location.countryCode.toLowerCase() : ''}.png`}
														size={'xs'}
													>
														<IconQuestionMark />
													</Avatar>

													<Text
														span
														variant="body2"
														c="var(--mantine-color-igluTextPrimary-9)"
													>
														{getCountryName(
															entity.location &&
																entity.location
																	.countryCode
																? entity.location
																		.countryCode
																: ''
														)}
													</Text>
												</Flex>
											</Flex>
											{entity?.legalIdentifier && (
												<Flex
													style={{
														width: '100%',
													}}
													flex={1}
													gap={'10px'}
												>
													<Flex flex={3}>
														<Text
															span
															variant="body2"
															c="var(--mantine-color-igluSecondary-9)"
														>
															{t(
																'projectDetails.producerLegalIdentifier'
															)}
														</Text>
													</Flex>
													<Flex flex={5}>
														<Text
															span
															variant="body2"
															c="var(--mantine-color-igluTextPrimary-9)"
														>
															{entity &&
															entity.legalIdentifier
																? entity.legalIdentifier
																: NOT_APPLICABLE}
														</Text>
													</Flex>
												</Flex>
											)}
										</Flex>
									</Flex>
								</Flex>
								<Flex
									bg={'#FDFDFD'}
									flex={1}
									direction={'column'}
									gap={'24px'}
								>
									<Flex className={classes.containerBloc}>
										<Title
											order={5}
											c="var(--mantine-color-igluPrimary-9)"
											mb={'24px'}
										>
											{t('global.description')}
										</Title>
										<Flex>
											<Text
												span
												variant="body1"
												c="var(--mantine-color-igluTextPrimary-9)"
											>
												{description ? (
													<Spoiler
														onExpandedChange={(opened) =>
															setIsExpanded(opened)
														}
														maxHeight={120}
														showLabel={t(
															'global.showMore'
														)}
														hideLabel={t('global.hide')}
														styles={{
															control: {
																fontFamily:
																	'InterDisplay-SemiBold',
																color:
																	'var(--mantine-color-igluPrimary-9)',
															},
														}}
													>
														<DescriptionRenderer
															description={description}
														/>
														{!isExpanded && (
															<div
																style={{
																	position: 'absolute',
																	bottom: 0,
																	left: 0,
																	width: '100%',
																	height: '40px',
																}}
															/>
														)}
													</Spoiler>
												) : (
													<Text
														span
														variant="body1"
														c="var(--mantine-color-igluTextPrimary-9)"
													>
														{NOT_APPLICABLE}
													</Text>
												)}
											</Text>
										</Flex>
									</Flex>
									<Flex className={classes.containerBloc}>
										<Title
											order={5}
											c="var(--mantine-color-igluPrimary-9)"
											mb={'24px'}
										>
											{t(
												'global.downloadTechnicalDocuments'
											)}
										</Title>
										<Flex>
											{publicSpecs &&
											Array.isArray(publicSpecs) &&
											publicSpecs.length > 0 ? (
												<Group>
													{publicSpecs.map(
														(
															fileMetaData: TFileMetadata
														) => (
															<DownloadTag
																label={
																	fileMetaData.filename
																}
																url={fileMetaData.link}
															/>
														)
													)}
												</Group>
											) : (
												<Text
													span
													variant="body1"
													c="var(--mantine-color-igluTextPrimary-9)"
												>
													{NOT_APPLICABLE}
												</Text>
											)}
										</Flex>
									</Flex>
								</Flex>
							</Flex>
						</Tabs.Panel>
					</Tabs>
				</Flex>
			</Box>
		</Box>
	);
};

export default MeasurabilityProjectsPublic;
